/**
 * FERP Portal - Unified Primitive Data Hook
 *
 * Provides a single hook for fetching and managing all 25 FERP primitives.
 *
 * 25 Primitives (5 phases × 5 each):
 *   DESIGN:    Workflow, Template, Dependency, Policy, Role
 *   CONFIGURE: Team, Actor, Validation, Contract, Constraint
 *   EXECUTE:   Instance, Task, Approval, Document, Queue
 *   MONITOR:   Event, Notification, Trace, Checkpoint, Transaction
 *   OPTIMIZE:  Audit, Analytics, Schedule, Resource, Integration
 *
 * CONSTRAINTS:
 * - Repository pattern via generated GraphQL hooks
 * - Multi-tenant isolation via useTenantId()
 * - Type-safe via generated types
 */

import { useMemo, useCallback } from 'react';
import { useTenantId } from '@/auth';
import {
  // DESIGN Phase
  useListWorkflowsQuery,
  useListTemplatesQuery,
  useListDependenciesByWorkflowQuery,
  useListPoliciesQuery,
  useListRolesQuery,
  // CONFIGURE Phase
  useListTeamsQuery,
  useListActorsQuery,
  useListValidationsQuery,
  useListContractsByStatusQuery,
  useListConstraintsQuery,
  // EXECUTE Phase
  useListInstancesQuery,
  useListTasksQuery,
  useListApprovalsByStatusQuery,
  useListDocumentsQuery,
  useListQueuesQuery,
  // MONITOR Phase
  useListEventsQuery,
  useListNotificationsForRecipientQuery,
  useListTracesByWorkflowInstanceQuery,
  useListCheckpointsByWorkflowQuery,
  useListTransactionsByTenantQuery,
  // OPTIMIZE Phase
  useListAuditsQuery,
  useListAnalyticsQuery,
  useListSchedulesQuery,
  useListResourcesQuery,
  useListIntegrationsQuery,
  // Fragment types
  type WorkflowFieldsFragment,
  type TemplateFieldsFragment,
  type DependencyFieldsFragment,
  type PolicyFieldsFragment,
  type RoleFieldsFragment,
  type TeamFieldsFragment,
  type ActorFieldsFragment,
  type ValidationFieldsFragment,
  type ContractFieldsFragment,
  type ConstraintFieldsFragment,
  type InstanceFieldsFragment,
  type TaskFieldsFragment,
  type ApprovalFieldsFragment,
  type DocumentFieldsFragment,
  type QueueFieldsFragment,
  type EventFieldsFragment,
  type NotificationFieldsFragment,
  type TraceFieldsFragment,
  type CheckpointFieldsFragment,
  type TransactionFieldsFragment,
  type AuditFieldsFragment,
  type AnalyticsFieldsFragment,
  type ScheduleFieldsFragment,
  type ResourceFieldsFragment,
  type IntegrationFieldsFragment,
  // Enums
  GqlTaskStatus,
  GqlContractStatus,
  GqlApprovalStatus,
} from '@/generated/graphql';

// ============================================================================
// TYPES
// ============================================================================

/** All 25 FERP primitives organized by lifecycle phase. */
export type PrimitiveType =
  // DESIGN Phase
  | 'workflow' | 'template' | 'dependency' | 'policy' | 'role'
  // CONFIGURE Phase
  | 'team' | 'actor' | 'validation' | 'contract' | 'constraint'
  // EXECUTE Phase
  | 'instance' | 'task' | 'approval' | 'document' | 'queue'
  // MONITOR Phase
  | 'event' | 'notification' | 'trace' | 'checkpoint' | 'transaction'
  // OPTIMIZE Phase
  | 'audit' | 'analytics' | 'schedule' | 'resource' | 'integration';

export type LifecyclePhase = 'DESIGN' | 'CONFIGURE' | 'EXECUTE' | 'MONITOR' | 'OPTIMIZE';

export type AnyPrimitive =
  | WorkflowFieldsFragment | TemplateFieldsFragment | DependencyFieldsFragment
  | PolicyFieldsFragment | RoleFieldsFragment
  | TeamFieldsFragment | ActorFieldsFragment | ValidationFieldsFragment
  | ContractFieldsFragment | ConstraintFieldsFragment
  | InstanceFieldsFragment | TaskFieldsFragment | ApprovalFieldsFragment
  | DocumentFieldsFragment | QueueFieldsFragment
  | EventFieldsFragment | NotificationFieldsFragment | TraceFieldsFragment
  | CheckpointFieldsFragment | TransactionFieldsFragment
  | AuditFieldsFragment | AnalyticsFieldsFragment | ScheduleFieldsFragment
  | ResourceFieldsFragment | IntegrationFieldsFragment;

export interface PrimitiveStats {
  total: number;
  active: number;
  lifeCritical: number;
  health: 'healthy' | 'degraded' | 'critical';
}

export interface PrimitiveData<T> {
  items: T[];
  loading: boolean;
  error?: Error;
  refetch: () => void;
  stats: PrimitiveStats;
}

export interface UnifiedPrimitiveData {
  // DESIGN Phase
  workflows: PrimitiveData<WorkflowFieldsFragment>;
  templates: PrimitiveData<TemplateFieldsFragment>;
  dependencies: PrimitiveData<DependencyFieldsFragment>;
  policies: PrimitiveData<PolicyFieldsFragment>;
  roles: PrimitiveData<RoleFieldsFragment>;
  // CONFIGURE Phase
  teams: PrimitiveData<TeamFieldsFragment>;
  actors: PrimitiveData<ActorFieldsFragment>;
  validations: PrimitiveData<ValidationFieldsFragment>;
  contracts: PrimitiveData<ContractFieldsFragment>;
  constraints: PrimitiveData<ConstraintFieldsFragment>;
  // EXECUTE Phase
  instances: PrimitiveData<InstanceFieldsFragment>;
  tasks: PrimitiveData<TaskFieldsFragment>;
  approvals: PrimitiveData<ApprovalFieldsFragment>;
  documents: PrimitiveData<DocumentFieldsFragment>;
  queues: PrimitiveData<QueueFieldsFragment>;
  // MONITOR Phase
  events: PrimitiveData<EventFieldsFragment>;
  notifications: PrimitiveData<NotificationFieldsFragment>;
  traces: PrimitiveData<TraceFieldsFragment>;
  checkpoints: PrimitiveData<CheckpointFieldsFragment>;
  transactions: PrimitiveData<TransactionFieldsFragment>;
  // OPTIMIZE Phase
  audits: PrimitiveData<AuditFieldsFragment>;
  analyticsItems: PrimitiveData<AnalyticsFieldsFragment>;
  schedules: PrimitiveData<ScheduleFieldsFragment>;
  resources: PrimitiveData<ResourceFieldsFragment>;
  integrations: PrimitiveData<IntegrationFieldsFragment>;
  // Meta
  loading: boolean;
  getPrimitivesByPhase: (phase: LifecyclePhase) => PrimitiveData<AnyPrimitive>[];
  getPrimitiveByType: (type: PrimitiveType) => PrimitiveData<AnyPrimitive>;
  refetchAll: () => void;
  totalStats: {
    total: number;
    lifeCritical: number;
    health: 'healthy' | 'degraded' | 'critical';
  };
}

// ============================================================================
// PHASE MAPPING
// ============================================================================

export const PRIMITIVE_PHASES: Record<PrimitiveType, LifecyclePhase> = {
  workflow: 'DESIGN', template: 'DESIGN', dependency: 'DESIGN', policy: 'DESIGN', role: 'DESIGN',
  team: 'CONFIGURE', actor: 'CONFIGURE', validation: 'CONFIGURE', contract: 'CONFIGURE', constraint: 'CONFIGURE',
  instance: 'EXECUTE', task: 'EXECUTE', approval: 'EXECUTE', document: 'EXECUTE', queue: 'EXECUTE',
  event: 'MONITOR', notification: 'MONITOR', trace: 'MONITOR', checkpoint: 'MONITOR', transaction: 'MONITOR',
  audit: 'OPTIMIZE', analytics: 'OPTIMIZE', schedule: 'OPTIMIZE', resource: 'OPTIMIZE', integration: 'OPTIMIZE',
};

export const PRIMITIVES_BY_PHASE: Record<LifecyclePhase, PrimitiveType[]> = {
  DESIGN: ['workflow', 'template', 'dependency', 'policy', 'role'],
  CONFIGURE: ['team', 'actor', 'validation', 'contract', 'constraint'],
  EXECUTE: ['instance', 'task', 'approval', 'document', 'queue'],
  MONITOR: ['event', 'notification', 'trace', 'checkpoint', 'transaction'],
  OPTIMIZE: ['audit', 'analytics', 'schedule', 'resource', 'integration'],
};

// ============================================================================
// HELPER
// ============================================================================

function createEmptyPrimitiveData<T>(): PrimitiveData<T> {
  return {
    items: [],
    loading: false,
    refetch: () => {},
    stats: { total: 0, active: 0, lifeCritical: 0, health: 'degraded' },
  };
}

// ============================================================================
// HOOK
// ============================================================================

const PAGE_SIZE = 100;

export function usePrimitiveData(): UnifiedPrimitiveData {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  // ── DESIGN Phase ──────────────────────────────────────────
  const workflowsQuery = useListWorkflowsQuery({ variables: { limit: PAGE_SIZE }, skip });
  const templatesQuery = useListTemplatesQuery({ variables: { limit: PAGE_SIZE }, skip });
  const dependenciesQuery = useListDependenciesByWorkflowQuery({
    variables: { workflowId: '', limit: PAGE_SIZE }, skip: true,
  });
  const policiesQuery = useListPoliciesQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const rolesQuery = useListRolesQuery({ variables: { limit: PAGE_SIZE }, skip });

  // ── CONFIGURE Phase ───────────────────────────────────────
  const teamsQuery = useListTeamsQuery({ variables: { limit: PAGE_SIZE }, skip });
  const actorsQuery = useListActorsQuery({ variables: { limit: PAGE_SIZE }, skip });
  const validationsQuery = useListValidationsQuery({ variables: { limit: PAGE_SIZE }, skip });
  const contractsQuery = useListContractsByStatusQuery({
    variables: { status: GqlContractStatus.ACTIVE, limit: PAGE_SIZE }, skip,
  });
  const constraintsQuery = useListConstraintsQuery({ variables: { limit: PAGE_SIZE }, skip });

  // ── EXECUTE Phase ─────────────────────────────────────────
  const instancesQuery = useListInstancesQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const tasksQuery = useListTasksQuery({ variables: { limit: PAGE_SIZE }, skip });
  const approvalsQuery = useListApprovalsByStatusQuery({
    variables: { tenantId, status: GqlApprovalStatus.PENDING, limit: PAGE_SIZE }, skip,
  });
  const documentsQuery = useListDocumentsQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const queuesQuery = useListQueuesQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });

  // ── MONITOR Phase ─────────────────────────────────────────
  const eventsQuery = useListEventsQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const notificationsQuery = useListNotificationsForRecipientQuery({
    variables: { recipientActorId: '', limit: PAGE_SIZE }, skip: true,
  });
  const tracesQuery = useListTracesByWorkflowInstanceQuery({
    variables: { tenantId, workflowInstanceId: '', limit: PAGE_SIZE }, skip: true,
  });
  const checkpointsQuery = useListCheckpointsByWorkflowQuery({
    variables: { workflowInstanceId: '', limit: PAGE_SIZE }, skip: true,
  });
  const transactionsQuery = useListTransactionsByTenantQuery({
    variables: { limit: PAGE_SIZE }, skip,
  });

  // ── OPTIMIZE Phase ────────────────────────────────────────
  const auditsQuery = useListAuditsQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const analyticsQuery = useListAnalyticsQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const schedulesQuery = useListSchedulesQuery({ variables: { limit: PAGE_SIZE }, skip });
  const resourcesQuery = useListResourcesQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });
  const integrationsQuery = useListIntegrationsQuery({ variables: { tenantId, limit: PAGE_SIZE }, skip });

  // ── Build primitive data objects ──────────────────────────

  const workflows = useMemo((): PrimitiveData<WorkflowFieldsFragment> => {
    const items = workflowsQuery.data?.listWorkflows?.items ?? [];
    return { items, loading: workflowsQuery.loading, error: workflowsQuery.error, refetch: workflowsQuery.refetch,
      stats: { total: items.length, active: items.filter((w) => w.active).length, lifeCritical: items.filter((w) => w.criticality === 'LIFE_CRITICAL').length, health: items.length === 0 ? 'degraded' : 'healthy' } };
  }, [workflowsQuery]);

  const templates = useMemo((): PrimitiveData<TemplateFieldsFragment> => {
    const items = templatesQuery.data?.listTemplates?.items ?? [];
    return { items, loading: templatesQuery.loading, error: templatesQuery.error, refetch: templatesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [templatesQuery]);

  const dependencies = useMemo((): PrimitiveData<DependencyFieldsFragment> => {
    const items = dependenciesQuery.data?.listDependenciesByWorkflow?.items ?? [];
    return { items, loading: dependenciesQuery.loading, error: dependenciesQuery.error, refetch: dependenciesQuery.refetch,
      stats: { total: items.length, active: items.filter((d) => d.active).length, lifeCritical: 0, health: 'healthy' } };
  }, [dependenciesQuery]);

  const policies = useMemo((): PrimitiveData<PolicyFieldsFragment> => {
    const items = policiesQuery.data?.listPolicies?.items ?? [];
    return { items, loading: policiesQuery.loading, error: policiesQuery.error, refetch: policiesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [policiesQuery]);

  const roles = useMemo((): PrimitiveData<RoleFieldsFragment> => {
    const items = rolesQuery.data?.listRoles?.items ?? [];
    return { items, loading: rolesQuery.loading, error: rolesQuery.error, refetch: rolesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [rolesQuery]);

  const teams = useMemo((): PrimitiveData<TeamFieldsFragment> => {
    const items = teamsQuery.data?.listTeams ?? [];
    return { items, loading: teamsQuery.loading, error: teamsQuery.error, refetch: teamsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [teamsQuery]);

  const actors = useMemo((): PrimitiveData<ActorFieldsFragment> => {
    const items = actorsQuery.data?.listActors?.items ?? [];
    return { items, loading: actorsQuery.loading, error: actorsQuery.error, refetch: actorsQuery.refetch,
      stats: { total: items.length, active: items.filter((a) => a.active).length, lifeCritical: 0, health: 'healthy' } };
  }, [actorsQuery]);

  const validations = useMemo((): PrimitiveData<ValidationFieldsFragment> => {
    const items = validationsQuery.data?.listValidations ?? [];
    return { items, loading: validationsQuery.loading, error: validationsQuery.error, refetch: validationsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [validationsQuery]);

  const contracts = useMemo((): PrimitiveData<ContractFieldsFragment> => {
    const items = contractsQuery.data?.listContractsByStatus?.items ?? [];
    return { items, loading: contractsQuery.loading, error: contractsQuery.error, refetch: contractsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [contractsQuery]);

  const constraints = useMemo((): PrimitiveData<ConstraintFieldsFragment> => {
    const items = constraintsQuery.data?.listConstraints?.items ?? [];
    return { items, loading: constraintsQuery.loading, error: constraintsQuery.error, refetch: constraintsQuery.refetch,
      stats: { total: items.length, active: items.filter((c) => c.active).length, lifeCritical: 0, health: 'healthy' } };
  }, [constraintsQuery]);

  const instances = useMemo((): PrimitiveData<InstanceFieldsFragment> => {
    const items = instancesQuery.data?.listInstances?.items ?? [];
    return { items, loading: instancesQuery.loading, error: instancesQuery.error, refetch: instancesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [instancesQuery]);

  const tasks = useMemo((): PrimitiveData<TaskFieldsFragment> => {
    const items = tasksQuery.data?.taskApiListTasks?.items ?? [];
    const failed = items.filter((t) => t.status === GqlTaskStatus.FAILED).length;
    return { items, loading: tasksQuery.loading, error: tasksQuery.error, refetch: tasksQuery.refetch,
      stats: { total: items.length, active: items.filter((t) => t.status === GqlTaskStatus.IN_PROGRESS).length, lifeCritical: 0, health: failed > 0 ? 'critical' : 'healthy' } };
  }, [tasksQuery]);

  const approvals = useMemo((): PrimitiveData<ApprovalFieldsFragment> => {
    const items = approvalsQuery.data?.listApprovalsByStatus?.items ?? [];
    return { items, loading: approvalsQuery.loading, error: approvalsQuery.error, refetch: approvalsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [approvalsQuery]);

  const documents = useMemo((): PrimitiveData<DocumentFieldsFragment> => {
    const items = documentsQuery.data?.listDocuments?.nodes ?? [];
    return { items, loading: documentsQuery.loading, error: documentsQuery.error, refetch: documentsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [documentsQuery]);

  const queues = useMemo((): PrimitiveData<QueueFieldsFragment> => {
    const items = queuesQuery.data?.listQueues?.items ?? [];
    return { items, loading: queuesQuery.loading, error: queuesQuery.error, refetch: queuesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [queuesQuery]);

  const events = useMemo((): PrimitiveData<EventFieldsFragment> => {
    const items = eventsQuery.data?.listEvents?.items ?? [];
    return { items, loading: eventsQuery.loading, error: eventsQuery.error, refetch: eventsQuery.refetch,
      stats: { total: items.length, active: 0, lifeCritical: 0, health: 'healthy' } };
  }, [eventsQuery]);

  const notifications = useMemo((): PrimitiveData<NotificationFieldsFragment> => {
    const items = notificationsQuery.data?.listNotificationsForRecipient?.items ?? [];
    return { items, loading: notificationsQuery.loading, error: notificationsQuery.error, refetch: notificationsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [notificationsQuery]);

  const traces = useMemo((): PrimitiveData<TraceFieldsFragment> => createEmptyPrimitiveData<TraceFieldsFragment>(), []);
  const checkpoints = useMemo((): PrimitiveData<CheckpointFieldsFragment> => createEmptyPrimitiveData<CheckpointFieldsFragment>(), []);

  const transactions = useMemo((): PrimitiveData<TransactionFieldsFragment> => {
    const items = transactionsQuery.data?.listTransactionsByTenant?.transactions ?? [];
    return { items, loading: transactionsQuery.loading, error: transactionsQuery.error, refetch: transactionsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [transactionsQuery]);

  const audits = useMemo((): PrimitiveData<AuditFieldsFragment> => {
    const items = auditsQuery.data?.listAudits?.audits ?? [];
    return { items, loading: auditsQuery.loading, error: auditsQuery.error, refetch: auditsQuery.refetch,
      stats: { total: items.length, active: 0, lifeCritical: 0, health: 'healthy' } };
  }, [auditsQuery]);

  const analyticsItems = useMemo((): PrimitiveData<AnalyticsFieldsFragment> => {
    const items = analyticsQuery.data?.listAnalytics?.nodes ?? [];
    return { items, loading: analyticsQuery.loading, error: analyticsQuery.error, refetch: analyticsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [analyticsQuery]);

  const schedules = useMemo((): PrimitiveData<ScheduleFieldsFragment> => {
    const items = schedulesQuery.data?.listSchedules?.items ?? [];
    return { items, loading: schedulesQuery.loading, error: schedulesQuery.error, refetch: schedulesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [schedulesQuery]);

  const resources = useMemo((): PrimitiveData<ResourceFieldsFragment> => {
    const items = resourcesQuery.data?.listResources?.items ?? [];
    return { items, loading: resourcesQuery.loading, error: resourcesQuery.error, refetch: resourcesQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [resourcesQuery]);

  const integrations = useMemo((): PrimitiveData<IntegrationFieldsFragment> => {
    const items = integrationsQuery.data?.listIntegrations?.nodes ?? [];
    return { items, loading: integrationsQuery.loading, error: integrationsQuery.error, refetch: integrationsQuery.refetch,
      stats: { total: items.length, active: items.length, lifeCritical: 0, health: 'healthy' } };
  }, [integrationsQuery]);

  // ── Collect ───────────────────────────────────────────────

  const allPrimitives = useMemo(() => ({
    workflows, templates, dependencies, policies, roles,
    teams, actors, validations, contracts, constraints,
    instances, tasks, approvals, documents, queues,
    events, notifications, traces, checkpoints, transactions,
    audits, analyticsItems, schedules, resources, integrations,
  }), [
    workflows, templates, dependencies, policies, roles,
    teams, actors, validations, contracts, constraints,
    instances, tasks, approvals, documents, queues,
    events, notifications, traces, checkpoints, transactions,
    audits, analyticsItems, schedules, resources, integrations,
  ]);

  const loading = useMemo(() => Object.values(allPrimitives).some((p) => p.loading), [allPrimitives]);

  const typeToKey: Record<PrimitiveType, keyof typeof allPrimitives> = {
    workflow: 'workflows', template: 'templates', dependency: 'dependencies', policy: 'policies', role: 'roles',
    team: 'teams', actor: 'actors', validation: 'validations', contract: 'contracts', constraint: 'constraints',
    instance: 'instances', task: 'tasks', approval: 'approvals', document: 'documents', queue: 'queues',
    event: 'events', notification: 'notifications', trace: 'traces', checkpoint: 'checkpoints', transaction: 'transactions',
    audit: 'audits', analytics: 'analyticsItems', schedule: 'schedules', resource: 'resources', integration: 'integrations',
  };

  const getPrimitivesByPhase = useCallback((phase: LifecyclePhase): PrimitiveData<AnyPrimitive>[] => {
    return PRIMITIVES_BY_PHASE[phase].map((type) => allPrimitives[typeToKey[type]] as PrimitiveData<AnyPrimitive>);
  }, [allPrimitives]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPrimitiveByType = useCallback((type: PrimitiveType): PrimitiveData<AnyPrimitive> => {
    return allPrimitives[typeToKey[type]] as PrimitiveData<AnyPrimitive>;
  }, [allPrimitives]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetchAll = useCallback(() => {
    Object.values(allPrimitives).forEach((p) => { if (p.refetch) p.refetch(); });
  }, [allPrimitives]);

  const totalStats = useMemo(() => {
    const all = Object.values(allPrimitives);
    const total = all.reduce((sum, p) => sum + p.stats.total, 0);
    const lifeCritical = all.reduce((sum, p) => sum + p.stats.lifeCritical, 0);
    const hasCritical = all.some((p) => p.stats.health === 'critical');
    const hasDegraded = all.some((p) => p.stats.health === 'degraded');
    const health: 'healthy' | 'degraded' | 'critical' = hasCritical ? 'critical' : hasDegraded ? 'degraded' : 'healthy';
    return { total, lifeCritical, health };
  }, [allPrimitives]);

  return { ...allPrimitives, loading, getPrimitivesByPhase, getPrimitiveByType, refetchAll, totalStats };
}

export default usePrimitiveData;
